import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useOtpVerification } from "../hooks/useOtpVerification";
import { useSendOtp } from "../hooks/useSendOtp";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@patient-watch/ui";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@patient-watch/ui";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@patient-watch/ui";
import { PageLayout } from "../components/PageLayout";
import { AlertCircle, Shield } from "lucide-react";
import { toast } from "@patient-watch/ui/sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useCopy } from "../i18n/locale";

export const Route = createFileRoute("/verify")({
    component: VerifyComponent,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            phone: (search.phone as string) || "",
        };
    },
});

// Zod schema for OTP validation
const otpSchema = (message: string) =>
    z.object({
        otp: z.string().length(6, message),
    });

type OtpFormData = z.infer<ReturnType<typeof otpSchema>>;

function VerifyComponent() {
    const { phone } = Route.useSearch();
    const { isAuthenticated } = useAuth();
    const router = useRouter();
    const queryClient = useQueryClient();
    const verifyOtpMutation = useOtpVerification();
    const sendOtpMutation = useSendOtp();
    const copy = useCopy();

    // Redirect if already authenticated
    useEffect(() => {
        console.log("Auth state changed:", { isAuthenticated });
        if (isAuthenticated) {
            console.log("Navigating to profile...");
            // Invalidate any auth-related queries to ensure fresh data
            queryClient.invalidateQueries({ queryKey: ["auth"] });
            queryClient.invalidateQueries({ queryKey: ["profile"] });
            queryClient.invalidateQueries({ queryKey: ["user"] });
            router.navigate({ to: "/profile" });
        }
    }, [isAuthenticated, router, queryClient]);

    const form = useForm<OtpFormData>({
        resolver: zodResolver(otpSchema(copy.verify.codeLength)),
        defaultValues: {
            otp: "",
        },
    });

    // Handle successful OTP sending
    useEffect(() => {
        if (sendOtpMutation.isSuccess) {
            toast.success(copy.verify.resent, {
                description: copy.verify.resentDescription,
            });
        }
    }, [sendOtpMutation.isSuccess]);

    // Handle OTP sending errors
    useEffect(() => {
        if (sendOtpMutation.error) {
            toast.error(copy.verify.resendFailed, {
                description: sendOtpMutation.error.message,
            });
        }
    }, [sendOtpMutation.error]);

    const handleSubmit = (data: OtpFormData) => {
        verifyOtpMutation.mutate({ phone, token: data.otp });
    };

    // Auto-submit when OTP is complete
    const handleOtpChange = (value: string) => {
        form.setValue("otp", value);

        // Auto-submit when 6 digits are entered
        if (value.length === 6) {
            setTimeout(() => {
                form.handleSubmit(handleSubmit)();
            }, 0);
        }
    };

    const handleResendOtp = () => {
        sendOtpMutation.mutate(phone);
    };

    return (
        <PageLayout title={copy.verify.title} description={copy.verify.description(phone)} icon={Shield} theme="amber">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
                    <FormField
                        control={form.control}
                        name="otp"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <InputOTP maxLength={6} value={field.value} onChange={handleOtpChange}>
                                        <InputOTPGroup>
                                            <InputOTPSlot index={0} />
                                            <InputOTPSlot index={1} />
                                            <InputOTPSlot index={2} />
                                            <InputOTPSlot index={3} />
                                            <InputOTPSlot index={4} />
                                            <InputOTPSlot index={5} />
                                        </InputOTPGroup>
                                    </InputOTP>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    {verifyOtpMutation.error && (
                        <div className="flex items-center gap-2 text-red-600 text-sm">
                            <AlertCircle size={20} />
                            {verifyOtpMutation.error.message}
                        </div>
                    )}
                    <Button type="submit" className="w-full" disabled={verifyOtpMutation.isPending}>
                        {verifyOtpMutation.isPending ? copy.verify.verifying : copy.verify.verify}
                    </Button>
                    <div className="flex justify-between items-center text-slate-600 text-sm">
                        <button
                            type="button"
                            onClick={handleResendOtp}
                            disabled={verifyOtpMutation.isPending || sendOtpMutation.isPending}
                            className="disabled:opacity-50 hover:text-slate-800 underline"
                        >
                            {sendOtpMutation.isPending ? copy.common.sending : copy.verify.resend}
                        </button>
                        <Link to="/login" className="hover:text-slate-800 underline">
                            {copy.verify.changePhone}
                        </Link>
                    </div>
                </form>
            </Form>
        </PageLayout>
    );
}
